import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const IDLE_MS = 30 * 60 * 1000; // 30 minutes

// Active only for the System Administrator (not while impersonating another role).
export default function AdminActivityTracker() {
  const { user, impersonating, logout } = useAuth();
  const location = useLocation();
  const timer = useRef(null);
  const isAdmin = !!user && user.role === "administrator" && !impersonating;

  // Log every page the admin visits + refresh the server-side idle timer.
  useEffect(() => {
    if (!isAdmin) return;
    api.post("/auth/activity", { path: location.pathname }).catch(() => {});
  }, [isAdmin, location.pathname]);

  // Client-side 30-minute inactivity auto-logout (server enforces it too).
  useEffect(() => {
    if (!isAdmin) return undefined;
    const reset = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(async () => {
        await logout();
        window.location.href = "/login?session=" +
          encodeURIComponent("You were signed out after 30 minutes of inactivity.");
      }, IDLE_MS);
    };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (timer.current) clearTimeout(timer.current);
    };
  }, [isAdmin, logout]);

  return null;
}
